// remix-skill: owner settings for a package (`/@user/name/settings`).
import { type Handle, css } from 'remix/component'
import { createMatcher } from 'remix/route-pattern/match'
import { readCurrentRouterHref } from '#client/client-router.tsx'
import {
	createRouteData,
	renderRoutePendingStatus,
	routeDataRedirect,
} from '#client/route-data.tsx'
import { readRouterPathname } from '#client/router-location.tsx'
import { readJson } from '#client/routes/account-approval-shared.ts'
import { NotFoundPage } from '#client/not-found-page.tsx'
import { resolvePackageListIconUrl } from '#universal/identity-icon-urls.ts'
import { type AccountPackageDetail } from '#universal/loader-data.ts'
import {
	fallbackDefaultBranchName,
	getPackageTreeHref,
} from '#universal/package-files.ts'
import { renderPackageRepoChrome } from '#universal/package-repo-nav.tsx'
import { routes } from '#universal/routes.ts'
import {
	type CommunityDetailApiPayload,
	type CommunityPackageMovedPayload,
	type PackageSettingsShell,
	consumePackageSettingsShell,
	getPackageSettingsPageRef,
	packageMoveDestination,
	postPackageLock,
	rememberListingId,
} from './community-detail-shared.ts'
import {
	detailArticleCss,
	renderMissingListing,
	renderOwnerPackageSection,
	renderShellStatus,
} from './community-detail-sections.tsx'
import { createPackageWebhooksController } from './package-webhook-settings.tsx'

const settingsMatcher = createMatcher(routes.communityPackageSettings.pattern)

export function PackageSettingsRoute(handle: Handle) {
	let ownerPackage: AccountPackageDetail | null = null
	let username = ''
	let kodyId = ''
	let isPrivate = false
	let listingId: string | null = null
	let defaultBranch: string | null = null
	let ownerProfilePublic = true
	let ownerDetailsMessage: string | null = null
	/** Payload last applied to the closure state above. */
	let appliedShell: PackageSettingsShell | null = null
	const lockInFlight = new Map<string, string | null>()
	const webhooks = createPackageWebhooksController(handle)
	const settingsData = createRouteData<
		'communityDetailShell',
		PackageSettingsShell
	>({
		consume: consumePackageSettingsShell,
		async load(href, signal) {
			const ref = getPackageSettingsPageRef(
				new URL(href, 'http://localhost').pathname,
			)
			if (!ref) return null
			const response = await fetch(ref.detailApiHref, {
				headers: { Accept: 'application/json' },
				signal,
			})
			const payload = await readJson<
				CommunityDetailApiPayload | CommunityPackageMovedPayload
			>(response)
			if (response.status === 401) return { kind: 'unauthorized' }
			if (response.status === 404) {
				const movedTo = payload && !payload.ok ? payload.redirectTo : null
				if (movedTo) {
					return routeDataRedirect(
						packageMoveDestination(ref.pathname, movedTo),
					)
				}
				return { kind: 'not-found' }
			}
			if (
				!response.ok ||
				!payload?.ok ||
				!payload.ownerPackage ||
				!payload.viewerIsOwner
			) {
				return { kind: 'not-found' }
			}
			if (payload.listing) {
				rememberListingId(ref.pathname, payload.listing.id)
			}
			return {
				kind: 'owner',
				ownerPackage: payload.ownerPackage,
				username: payload.username,
				kodyId: payload.kodyId || payload.ownerPackage.kodyId,
				isPrivate: payload.isPrivate ?? payload.ownerPackage.isPrivate,
				listingId: payload.listing?.id ?? null,
				defaultBranch: payload.listing?.defaultBranch ?? null,
				ownerProfilePublic: payload.ownerProfilePublic,
			}
		},
	})

	function applyOwnerPackageLock(packageId: string, lockedAt: string | null) {
		if (ownerPackage?.id === packageId) {
			ownerPackage = { ...ownerPackage, lockedAt }
		}
	}

	async function togglePackageLock() {
		if (!ownerPackage || lockInFlight.has(ownerPackage.id)) return
		const packageId = ownerPackage.id
		const previousLockedAt = ownerPackage.lockedAt
		const nextLocked = !(
			typeof previousLockedAt === 'string' && previousLockedAt.trim().length > 0
		)
		const nextLockedAt = nextLocked ? new Date().toISOString() : null
		lockInFlight.set(packageId, nextLockedAt)
		ownerDetailsMessage = null
		applyOwnerPackageLock(packageId, nextLockedAt)
		handle.update()

		const result = await postPackageLock(packageId, nextLocked)
		lockInFlight.delete(packageId)
		if (result.status === 'unauthorized') {
			window.location.assign('/login')
			handle.update()
			return
		}
		if (result.status === 'error') {
			applyOwnerPackageLock(packageId, previousLockedAt)
			if (ownerPackage?.id === packageId) {
				ownerDetailsMessage = result.message
			}
			handle.update()
			return
		}
		applyOwnerPackageLock(packageId, result.lockedAt ?? nextLockedAt)
		if (result.selectedPackage?.id === packageId) {
			ownerPackage = result.selectedPackage
		}
		handle.update()
	}

	return () => {
		const currentHref = readCurrentRouterHref(handle)
		const pathname = readRouterPathname(handle)
		const ref = getPackageSettingsPageRef(pathname)
		const urlParams = settingsMatcher.match(
			new URL(pathname, 'http://localhost'),
		)?.params

		if (!ref) {
			return <NotFoundPage />
		}

		const snapshot = settingsData.read(handle, currentHref)
		if (snapshot.data && snapshot.data !== appliedShell) {
			appliedShell = snapshot.data
			switch (snapshot.data.kind) {
				case 'owner':
					ownerPackage = snapshot.data.ownerPackage
					username = snapshot.data.username
					kodyId = snapshot.data.kodyId
					isPrivate = snapshot.data.isPrivate
					listingId = snapshot.data.listingId
					defaultBranch = snapshot.data.defaultBranch
					if (snapshot.data.ownerProfilePublic !== undefined) {
						ownerProfilePublic = snapshot.data.ownerProfilePublic
					}
					ownerDetailsMessage = null
					break
				case 'unauthorized':
				case 'not-found':
					break
				default: {
					const exhaustive: never = snapshot.data
					throw new Error(`Unhandled settings shell: ${String(exhaustive)}`)
				}
			}
		}

		if (snapshot.data?.kind === 'unauthorized' && !snapshot.stale) {
			return renderMissingListing(
				'Unauthorized',
				'You are not allowed to view this page.',
			)
		}
		if (
			(snapshot.data?.kind === 'not-found' && !snapshot.stale) ||
			snapshot.kind === 'not-found'
		) {
			return <NotFoundPage />
		}

		const pending = snapshot.kind === 'pending'
		// The previous package's settings (`snapshot.stale`) stay on screen
		// while a fallback fetch runs; the loading copy is for the cold path.
		const showReady = snapshot.data?.kind === 'owner'
		if (showReady && username && kodyId && typeof document !== 'undefined') {
			handle.queueTask(() => webhooks.ensureLoaded({ username, kodyId }))
		}
		const showError = snapshot.kind === 'error'
		const statusMessage = showError
			? 'Unable to load package settings.'
			: showReady
				? ''
				: 'Loading package settings…'

		const chromeUsername = username || urlParams?.username || ''
		const chromeKodyId = kodyId || urlParams?.kodyId || ''

		return (
			<article
				mix={css(detailArticleCss)}
				data-testid="package-settings"
				aria-busy={pending && showReady ? 'true' : undefined}
			>
				{pending && showReady ? renderRoutePendingStatus() : null}
				{chromeUsername && chromeKodyId
					? renderPackageRepoChrome({
							username: chromeUsername,
							kodyId: chromeKodyId,
							isPrivate,
							isListed: ownerPackage?.hasCommunityListing === true,
							viewerIsOwner: true,
							active: 'settings',
							filesHref: getPackageTreeHref({
								username: chromeUsername,
								kodyId: chromeKodyId,
								listingId,
								ref: defaultBranch || fallbackDefaultBranchName,
							}),
							description: ownerPackage?.description ?? '',
							ownerProfilePublic,
							iconUrl: resolvePackageListIconUrl({
								username: chromeUsername,
								kodyId: chromeKodyId,
								listingId,
								listingIconCommit: null,
								publishedCommit: ownerPackage?.publishedCommit ?? null,
							}),
							iconName: ownerPackage?.kodyId ?? chromeKodyId,
						})
					: null}
				{renderShellStatus(statusMessage)}
				{showReady && ownerPackage
					? renderOwnerPackageSection({
							ownerUsername: username,
							ownerPackage,
							lockInFlight: lockInFlight.has(ownerPackage.id),
							ownerDetailsMessage,
							onToggleLock: () => void togglePackageLock(),
							onPackagesPayload: (payload) => {
								const selected = payload.selectedPackage
								if (selected && selected.id === ownerPackage?.id) {
									ownerPackage = selected
									isPrivate = selected.isPrivate
									handle.update()
								}
							},
						})
					: null}
				{showReady && ownerPackage
					? webhooks.render({ username, kodyId })
					: null}
			</article>
		)
	}
}
